/**
 * カスタムリンク機能を管理
 *
 * 仕様
 * - role="link" が付与された要素をリンクとして扱う
 *   - クリック操作: <a> と同様に動作させる
 *   - キーボード操作: <a> と同様に動作させる（Enterキーでの起動）
 * - tabindex="0" が設定されていなければ付与する
 * - data-href 属性を href 属性として扱う
 * - data-target 属性を target 属性として扱う
 *
 * 使い方
 * <div role="link" data-href="https://example.com" data-target="_blank">
 *   カスタムリンク
 * </div>
 *
 * 追加機能01:
 * - data-href と data-breakpoint が付与されているものについては、ブレークポイント以下の場合下記の仕様とする
 *   - data-breakpoint の値は、ブレークポイントの値を指定する
 *   - ブレークポイント以下の場合
 *     - role="link" を付与する
 *     - data-href 属性を href 属性として扱う
 *     - data-target 属性を target 属性として扱う
 *     - tabindex 属性を 0 として扱う
 *     - クリックイベントを追加する
 *     - キーボードイベントを追加する
 *     - スペースキーでのデフォルトスクロールを防止する
 *   - ブレークポイントより大きい場合
 *     - role="link" を付与しない (あったら削除する)
 *     - リンクとしての機能は持たない
 */

/**
 * 1つのカスタムリンクを管理するクラス
 */
export class CustomLink {
  private element: HTMLElement;
  private href: string;
  private target: string;
  private boundHandleClick: (event: MouseEvent) => void;
  private boundHandleKeyPress: (event: KeyboardEvent) => void;
  private boundHandleKeyDown: (event: KeyboardEvent) => void;

  constructor(element: HTMLElement) {
    if (element.getAttribute('role') !== 'link') {
      throw new Error(
        'CustomLink can only be initialized on elements with role="link"'
      );
    }

    this.element = element;
    this.href = this.element.getAttribute('data-href') || '#';
    this.target = this.element.getAttribute('data-target') || '_self';

    // tabindex="0" が設定されていなければ付与
    if (!this.element.hasAttribute('tabindex')) {
      this.element.setAttribute('tabindex', '0');
    }

    // バインドしたメソッドを保持してremoveEventListener時に使用
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleKeyPress = this.handleKeyPress.bind(this);
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);

    this.init();
  }

  private init(): void {
    // クリックイベントを追加
    this.element.addEventListener('click', this.boundHandleClick);

    // キーボードイベントを追加
    this.element.addEventListener('keypress', this.boundHandleKeyPress);
    this.element.addEventListener('keydown', this.boundHandleKeyDown);
  }

  private handleClick(event: MouseEvent): void {
    event.preventDefault();
    this.navigate();
  }

  private handleKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.navigate();
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // スペースキーでのデフォルトスクロールを防止
    if (event.key === ' ') {
      event.preventDefault();
    }
  }

  private navigate(): void {
    if (this.target === '_blank') {
      window.open(this.href, this.target);
    } else {
      window.location.href = this.href;
    }
  }

  public destroy(): void {
    this.element.removeEventListener('click', this.boundHandleClick);
    this.element.removeEventListener('keypress', this.boundHandleKeyPress);
    this.element.removeEventListener('keydown', this.boundHandleKeyDown);
  }
}

/**
 * 複数のカスタムリンクを管理するマネージャークラス
 */
export class CustomLinkManager {
  private customLinks: Map<HTMLElement, CustomLink> = new Map();
  private elementsWithBreakpoint: Set<HTMLElement> = new Set();
  private boundHandleResize: () => void;

  constructor() {
    this.boundHandleResize = this.handleResize.bind(this);
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeCustomLinks();
        window.addEventListener('resize', this.boundHandleResize);
        this.handleResize(); // 初期チェック
      });
    } else {
      this.initializeCustomLinks();
      window.addEventListener('resize', this.boundHandleResize);
      this.handleResize(); // 初期チェック
    }
  }

  private initializeCustomLinks(): void {
    // data-href と data-breakpoint を持つ要素を選択
    const breakpointElements = document.querySelectorAll<HTMLElement>(
      '[data-href][data-breakpoint]'
    );

    // role="link" を持ち、data-breakpoint を持たない要素を選択
    const linkElements = document.querySelectorAll<HTMLElement>(
      '[role="link"]:not([data-href][data-breakpoint])'
    );

    // data-href と data-breakpoint を持つ要素を管理対象に追加
    breakpointElements.forEach((element) => {
      this.elementsWithBreakpoint.add(element);
    });

    // role="link" だけを持つ要素を処理
    linkElements.forEach((element) => {
      this.addCustomLink(element);
    });
  }

  private addCustomLink(element: HTMLElement): void {
    if (this.customLinks.has(element)) return;

    try {
      const customLink = new CustomLink(element);
      this.customLinks.set(element, customLink);
    } catch (error) {
      console.warn(error);
    }
  }

  private removeCustomLink(element: HTMLElement): void {
    const customLink = this.customLinks.get(element);
    if (customLink) {
      customLink.destroy();
      this.customLinks.delete(element);
    }
  }

  private handleResize(): void {
    this.elementsWithBreakpoint.forEach((element) => {
      const breakpointStr = element.getAttribute('data-breakpoint');
      const breakpoint = breakpointStr ? parseInt(breakpointStr, 10) : 960; // デフォルト960

      if (isNaN(breakpoint)) {
        console.warn(`Invalid data-breakpoint value: ${breakpointStr}`);
        return;
      }

      if (window.innerWidth <= breakpoint) {
        // ブレークポイント以下の場合リンクとして機能させる
        if (element.getAttribute('role') !== 'link') {
          element.setAttribute('role', 'link');

          // tabindex="0" が設定されていなければ付与
          if (!element.hasAttribute('tabindex')) {
            element.setAttribute('tabindex', '0');
          }

          this.addCustomLink(element);
        }
      } else {
        // ブレークポイントより大きい場合リンク機能を無効化
        if (element.hasAttribute('role')) {
          element.removeAttribute('role');
        }
        if (element.hasAttribute('tabindex')) {
          element.removeAttribute('tabindex');
        }
        this.removeCustomLink(element);
      }
    });
  }

  public getCustomLinks(): CustomLink[] {
    return Array.from(this.customLinks.values());
  }

  public destroy(): void {
    this.customLinks.forEach((customLink) => customLink.destroy());
    this.customLinks.clear();

    window.removeEventListener('resize', this.boundHandleResize);
    this.elementsWithBreakpoint.clear();
  }
}
